import React from 'react';
import PropTypes from 'prop-types';
import { FormInput, FormTextEditor, Button, Message } from '@getro/rombo';
import { Box, Flex } from 'rebass/styled-components';
import { Field, Form, Formik } from 'formik';
import { TalentSetting } from './components/talentSetting';
import { TalentDivider } from './talentDivider';

export const TalentNetworkSettings = ({ talentNetwork, error, update, isUpdating }) => {
  const initialValues = {
    tnV2TabIntroductionTitle: talentNetwork.tnV2TabIntroductionTitle || 'Find your next opportunity',
    tnV2TabIntroductionText:
      talentNetwork.tnV2TabIntroductionText || `Get connected to the right company at the right time.`,
    tnV2SuccessMessageTitle: !talentNetwork.tnV2SuccessMessageTitle
      ? 'Welcome to our talent network'
      : talentNetwork.tnV2SuccessMessageTitle,
    tnV2SuccessMessageText: !talentNetwork.tnV2SuccessMessageText
      ? `Thanks for signaling your interest, your profile is now visible to our team and companies in our network.
    We’ll contact you when we find a great opportunity for you.`
      : talentNetwork.tnV2SuccessMessageText,
    tnSendWelcomeEmail: typeof talentNetwork.tnSendWelcomeEmail === 'boolean' ? talentNetwork.tnSendWelcomeEmail : true,
  };

  const validate = (values) =>
    Object.keys(values).reduce((acc, key) => {
      if (typeof values[key] === 'boolean') {
        return acc;
      }

      if (!values[key] || !values[key].trim()) {
        acc[key] = 'This field is required';
        return acc;
      }

      return acc;
    }, {});

  return (
    <Formik validate={validate} enableReinitialize={false} initialValues={initialValues} onSubmit={update}>
      {({ handleSubmit, isValid, setFieldValue, setFieldTouched, values }) => (
        <>
          <TalentSetting title="Welcome new members" sx={{ mb: '40px' }} />
          <TalentSetting
            toggleTalentNetwork={() => {
              setFieldValue('tnSendWelcomeEmail', !values.tnSendWelcomeEmail);
              update({
                ...values,
                tnSendWelcomeEmail: !values.tnSendWelcomeEmail,
              });
            }}
            tnHidden={values.tnSendWelcomeEmail}
            title="Send welcome email on signup"
            description="Automatically send a confirmation email with next steps when someone joins your Talent Network."
          />
          <TalentDivider />
          <Flex sx={{ flexDirection: 'column', rowGap: '24px' }}>
            <TalentSetting title="Content and messaging customization" sx={{ mb: '16px' }} />
            <Form>
              <Flex sx={{ flexDirection: 'column', rowGap: '40px' }}>
                <Flex sx={{ flexDirection: 'column', rowGap: '16px' }}>
                  <TalentSetting
                    title="Talent network tab content"
                    description="Inspire candidates to join by customizing your Talent Network tab."
                  />
                  <Field name="tnV2TabIntroductionTitle" component={FormInput} label="Title" labelExtra="Required" />
                  <Field
                    component={FormTextEditor}
                    name="tnV2TabIntroductionText"
                    label="Content"
                    options={['bold', 'italic', 'h1', 'h2', 'list', 'link']}
                    labelExtra="Required"
                    onChange={(value) => {
                      setFieldTouched('tnV2TabIntroductionText');
                      setFieldValue('tnV2TabIntroductionText', value);
                    }}
                  />
                </Flex>

                <Flex sx={{ flexDirection: 'column', rowGap: '16px' }}>
                  <TalentSetting
                    title="Success message content"
                    description="Customize the message shown right after someone joins your Talent Network."
                  />
                  <Field name="tnV2SuccessMessageTitle" component={FormInput} label="Title" labelExtra="Required" />
                  <Field
                    component={FormTextEditor}
                    name="tnV2SuccessMessageText"
                    label="Content"
                    options={['bold', 'italic', 'h1', 'h2', 'list', 'link']}
                    labelExtra="Required"
                    onChange={(value) => {
                      setFieldTouched('tnV2SuccessMessageText');
                      setFieldValue('tnV2SuccessMessageText', value);
                    }}
                  />
                </Flex>
                {error && (
                  <Box mt="24px">
                    <Message type="error" content={error} />
                  </Box>
                )}
              </Flex>
              <Flex mt="24px">
                <Button size="small" disabled={!isValid} type="submit" loading={isUpdating} onClick={handleSubmit}>
                  Save
                </Button>
              </Flex>
            </Form>
          </Flex>
        </>
      )}
    </Formik>
  );
};

TalentNetworkSettings.propTypes = {
  talentNetwork: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  error: PropTypes.string,
  isUpdating: PropTypes.bool.isRequired,
};

TalentNetworkSettings.defaultProps = {
  error: null,
};

export default TalentNetworkSettings;
